import React, { useState, useMemo, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FormControl, TextField, FormHelperText } from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, configApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import yaml from 'js-yaml';
import nunjucks from 'nunjucks';
import debounce from 'lodash.debounce';

// Configure nunjucks with custom delimiters.
nunjucks.configure({
  autoescape: false,
  tags: {
    variableStart: '${{',
    variableEnd: '}}',
  },
});

export const CreateAdoProduct = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, uiSchema, formContext } = props;

  // internalValue holds the text currently displayed in the input.
  const [internalValue, setInternalValue] = useState(formData || '');
  // validationError is used to show errors in the UI.
  const [validationError, setValidationError] = useState<string | null>(null);

  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    const fetchBaseUrl = async () => {
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      setBaseUrl(baseUrl);
    };
    fetchBaseUrl();
  }, [discoveryApi]);

  const [baseUrl, setBaseUrl] = useState('');

  // Render the adoProject template using the current form context.
  const adoProjectTemplate = uiSchema['ui:options']?.adoProject || '';
  const resolvedAdoProject = nunjucks
    .renderString(adoProjectTemplate, formContext.formData)
    .trim();
  console.log('CreateAdoProduct: resolved adoProject =', resolvedAdoProject);

  /**
   * checkProductExists:
   * Fetches the list of products from AWS Parameter Store for the resolved ADO project,
   * and returns true if the product already exists.
   */
  const checkProductExists = async (value: string): Promise<boolean> => {
    if (!resolvedAdoProject) {
      // No project provided—skip validation.
      return false;
    }
    try {
      const credentials = await identityApi.getCredentials();
      const token = credentials.token;
      const response = await axios.post(
        `${baseUrl}/aws/params`,
        { name: `/backstage/project/${resolvedAdoProject}` },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const rawValue = response.data.value || '';
      let productList: string[] = [];
      if (rawValue) {
        const parsed = yaml.load(rawValue);
        if (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).products)) {
          productList = (parsed as any).products;
        } else if (typeof parsed === 'string') {
          productList = parsed
            .split(',')
            .map((p: string) => p.trim())
            .filter((p: string) => p);
        }
      }
      return productList.includes(value);
    } catch (error) {
      console.error('Error checking product existence:', error);
      return false;
    }
  };

  // Debounced async validation function.
  // It checks for empty input, then verifies whether the product already exists.
  // If validation passes, it updates the parent's value.
  // Otherwise, it keeps the parent's value empty.
  const debouncedValidate = useMemo(
    () =>
      debounce(async (value: string) => {
        if (!value.trim()) {
          setValidationError('Product field is required.');
          onChange('');
          return;
        }
        const exists = await checkProductExists(value);
        if (exists) {
          setValidationError('Product already exists in AWS Parameter Store.');
          onChange('');
        } else {
          setValidationError(null);
          onChange(value);
        }
      }, 800),
    [onChange, resolvedAdoProject]
  );

  // On every keystroke, update the internal value and clear the parent's value immediately.
  // This avoids retaining an old valid value while a new value is being typed.
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInternalValue(value);
    // Immediately clear the parent's value to avoid stale data.
    onChange('');
    
    // Check if value contains only lowercase letters
    if (value && !/^[a-z]*$/.test(value)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      return;
    }
    
    setValidationError(null);
    debouncedValidate(value);
  };

  // On blur, flush any pending debounced validation and perform an immediate check.
  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    debouncedValidate.cancel();
    const value = event.target.value.trim();
    if (!value) {
      setValidationError('Product field is required.');
      onChange('');
      return;
    }
    
    // Check if value contains only lowercase letters
    if (!/^[a-z]+$/.test(value)) {
      setValidationError('Only lowercase letters (a-z) are allowed. No spaces, capitals, or special characters.');
      onChange('');
      return;
    }
    
    const exists = await checkProductExists(value);
    if (exists) {
      setValidationError('Product already exists in AWS Parameter Store.');
      onChange('');
    } else {
      setValidationError(null);
      onChange(value);
    }
  };

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!validationError}>
      <TextField
        required={required} // Ensures the UI shows the required asterisk.
        label={schema.title || 'Product Name'}
        helperText={validationError || schema.description}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={!!rawErrors?.length || !!validationError}
      />
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
